import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

export enum SigningProgressItemStatus {
    Pending = "Pending",
    InProgress = "InProgress",
    Done = "Done",
    Error = "Error",
}

export enum SigningProgressItemKey {
    Verdict = "Verdict",
    PrepareDocuments = "PrepareDocuments",
    SignDocuments = "SignDocuments",
}

export interface SigningProgressItem {
    key: SigningProgressItemKey;
    title: string;
    status: SigningProgressItemStatus;
    error?: string;
}

export interface SigningProgressState {
    items: SigningProgressItem[];
    message?: string;
    closable: boolean;
    /** Повтор упавших шагов по справкам. Кнопка «Повторить» есть, только когда окно можно закрыть. */
    onRetry?: () => void;
}

export const SIGNING_PROGRESS_TITLES: Record<SigningProgressItemKey, string> = {
    [SigningProgressItemKey.Verdict]: "Подписание медосмотра",
    [SigningProgressItemKey.PrepareDocuments]: "Формирование направлений к врачу",
    [SigningProgressItemKey.SignDocuments]: "Подписание направлений к врачу",
};

export const WORKPLACE_DOCUMENTS_HINT = "Медосмотр завершён. Направления можно подписать в разделе «Направления на подпись».";
export const VERDICT_COMPLETED_MESSAGE = "Медосмотр завершён.";

export const PREPARE_DOCUMENTS_ERROR = "Не удалось сформировать направления. Попробуйте ещё раз или подпишите позже в разделе «Направления на подпись».";
export const SIGN_DOCUMENTS_ERROR = "Не удалось подписать направления. Попробуйте ещё раз или подпишите позже в разделе «Направления на подпись».";
export const CREATE_DOCUMENTS_ERROR = "Медосмотр подписан, но направления к врачу не созданы. Обратитесь в техподдержку.";

interface SigningProgressDialogProps {
    state: SigningProgressState | null;
    onClose: () => void;
}

const buttonSx = {
    borderColor: '#9588e8',
    color: '#9588e8',
    '&:hover': {
        borderColor: '#9588e8',
        backgroundColor: 'rgba(149, 136, 232, 0.08)',
    },
};

const renderStatusIcon = (status: SigningProgressItemStatus) => {
    switch (status) {
        case SigningProgressItemStatus.InProgress:
            return <CircularProgress size={22} sx={{ color: '#9588e8' }} />;
        case SigningProgressItemStatus.Done:
            return <CheckCircleIcon sx={{ color: '#5bba64' }} />;
        case SigningProgressItemStatus.Error:
            return <ErrorOutlineIcon sx={{ color: '#f44336' }} />;
        default:
            return <RadioButtonUncheckedIcon sx={{ color: '#bdbdbd' }} />;
    }
};

/**
 * Окно прогресса подписания медосмотра физическим токеном и справок по нему.
 * Пока идёт подписание, окно не закрывается.
 */
export const SigningProgressDialog: React.FC<SigningProgressDialogProps> = ({ state, onClose }) => {
    const open = state !== null;
    const closable = state?.closable ?? false;

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (closable && reason !== 'backdropClick') {
                    onClose();
                }
            }}
            disableEscapeKeyDown={!closable}
            maxWidth="xs"
            fullWidth
            sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
        >
            <DialogTitle sx={{ textAlign: 'center' }}>Подписание</DialogTitle>
            <DialogContent sx={{ py: 2 }}>
                {state?.items.map((item) => (
                    <Box key={item.key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1 }}>
                        <Box sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {renderStatusIcon(item.status)}
                        </Box>
                        <Box>
                            <Typography
                                variant="body1"
                                sx={{ fontWeight: item.status === SigningProgressItemStatus.InProgress ? 'bold' : 'normal' }}
                            >
                                {item.title}
                            </Typography>
                            {item.error && (
                                <Typography variant="body2" sx={{ color: '#f44336', whiteSpace: 'pre-line' }}>
                                    {item.error}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                ))}
                {state?.message && (
                    <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
                        {state.message}
                    </Typography>
                )}
            </DialogContent>
            {closable && (
                <DialogActions sx={{ justifyContent: 'center', pb: 2, gap: 1 }}>
                    {state?.onRetry && (
                        <Button onClick={state.onRetry} variant="outlined" sx={buttonSx}>
                            Повторить
                        </Button>
                    )}
                    <Button onClick={onClose} variant="outlined" sx={buttonSx}>
                        Закрыть
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};
